import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCheck, Download, Pencil, Paperclip, Archive, Trash2 } from "lucide-react";
import { api, API, getToken, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function VerwaltungTeilnehmer() {
  const [courses, setCourses] = useState([]);
  const [cid, setCid] = useState("");
  const [regs, setRegs] = useState([]);
  const [editReg, setEditReg] = useState(null);

  useEffect(() => { api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {}); }, []);
  const loadRegs = (id) => api.get(`/courses/${id}/registrations`).then(({ data }) => setRegs(data)).catch(() => {});
  useEffect(() => { if (cid) loadRegs(cid); else setRegs([]); }, [cid]);

  const saveEdit = async () => {
    try {
      await api.put(`/registrations/${editReg.id}/manage`, { kind_name: editReg.kind_name, status: editReg.status });
      toast.success("Gespeichert."); setEditReg(null); loadRegs(cid);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleStatus = async (r) => {
    const status = r.status === "abgemeldet" ? "angemeldet" : "abgemeldet";
    await api.put(`/registrations/${r.id}/manage`, { status });
    toast.success(status === "abgemeldet" ? "Abgemeldet." : "Wieder angemeldet."); loadRegs(cid);
  };
  const del = async (r) => {
    if (!window.confirm(`Anmeldung von „${r.kind_name || r.user_email}" wirklich löschen? Dies kann nicht rückgängig gemacht werden.`)) return;
    try {
      await api.delete(`/registrations/${r.id}`);
      toast.success("Anmeldung gelöscht."); loadRegs(cid);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const exportUrl = `${API}/admin/export/registrations?token=${getToken()}${cid ? `&course_id=${cid}` : ""}`;
  const zipUrl = cid ? `${API}/admin/export/files?token=${getToken()}&course_id=${cid}` : null;

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3"><UserCheck className="text-primary" /><h1 className="text-3xl font-serif">Teilnehmer</h1></div>
        <div className="flex items-center gap-2">
          {zipUrl && <a href={zipUrl} data-testid="export-zip-button"><Button variant="outline"><Archive size={16} className="mr-1" /> Dateien (ZIP)</Button></a>}
          {cid
            ? <a href={exportUrl} data-testid="export-xlsx-button"><Button><Download size={16} className="mr-1" /> Export XLSX</Button></a>
            : <Button disabled variant="outline" title="Bitte zuerst einen Kurs wählen" data-testid="export-xlsx-button-disabled"><Download size={16} className="mr-1" /> Export XLSX</Button>}
        </div>
      </div>
      <p className="text-muted-foreground mb-6 text-sm">Angemeldete Personen je Kurs – bearbeitbar und ab-/anmeldbar.</p>

      <div className="max-w-sm mb-6">
        <Label className="text-xs">Kurs</Label>
        <Select value={cid} onValueChange={setCid}>
          <SelectTrigger data-testid="verw-course-filter"><SelectValue placeholder="Kurs wählen" /></SelectTrigger>
          <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.kurs_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {cid && (
        <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Name (Kind)</TableHead><TableHead>Eltern-E-Mail</TableHead><TableHead>Status</TableHead><TableHead>Dateien</TableHead><TableHead className="text-right">Aktionen</TableHead></TableRow></TableHeader>
            <TableBody>
              {regs.map((r) => (
                <TableRow key={r.id} data-testid={`verw-reg-${r.id}`}>
                  <TableCell>{r.kind_name || "—"}</TableCell>
                  <TableCell className="text-sm">{r.user_email}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "abgemeldet" ? "bg-slate-100 text-slate-500" : "bg-green-50 text-green-700"}`}>{r.status || "angemeldet"}</span>
                  </TableCell>
                  <TableCell>
                    {Object.entries(r.uploads || {}).length === 0
                      ? <span className="text-muted-foreground text-xs">—</span>
                      : <div className="flex flex-wrap gap-2">{Object.entries(r.uploads || {}).map(([fid, fileId]) => (
                          <a key={fileId} href={`${API}/files/${fileId}/download?token=${getToken()}`} target="_blank" rel="noreferrer" title={r.answers?.[fid] || "Datei herunterladen"} className="text-primary inline-flex items-center hover:underline" data-testid={`verw-file-${r.id}-${fid}`}><Paperclip size={16} /></a>
                        ))}</div>}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditReg({ id: r.id, kind_name: r.kind_name || "", status: r.status || "angemeldet" })} data-testid={`verw-edit-${r.id}`}><Pencil size={16} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleStatus(r)} data-testid={`verw-toggle-${r.id}`}>{r.status === "abgemeldet" ? "Anmelden" : "Abmelden"}</Button>
                    <a href={`${API}/registrations/${r.id}/pdf?token=${getToken()}`} target="_blank" rel="noreferrer" className="text-primary text-sm hover:underline">PDF</a>
                    <Button variant="ghost" size="sm" onClick={() => del(r)} data-testid={`verw-delete-${r.id}`}><Trash2 size={16} className="text-destructive" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {regs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Keine Anmeldungen in diesem Kurs.</TableCell></TableRow>}
            </TableBody>
          </Table>
          </div>
        </div>
      )}

      <Dialog open={!!editReg} onOpenChange={(o) => !o && setEditReg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Anmeldung bearbeiten</DialogTitle></DialogHeader>
          {editReg && (
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Name (Kind)</Label><Input value={editReg.kind_name} onChange={(e) => setEditReg({ ...editReg, kind_name: e.target.value })} data-testid="verw-edit-name" /></div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editReg.status} onValueChange={(v) => setEditReg({ ...editReg, status: v })}>
                  <SelectTrigger data-testid="verw-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="angemeldet">angemeldet</SelectItem><SelectItem value="abgemeldet">abgemeldet</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} data-testid="verw-save-button">Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
